import { useState, useEffect } from "react";
import { FiEdit2 } from "react-icons/fi";
import Modal from "newui/components/ui/Modal/Modal";
import type { ReviewClarification } from "../../../../types/clarification-review.type";
import { ChangeTrackingView } from "./ChangeTrackingView";
import { TriageStatusBadge } from "./TriageStatusBadge";

interface EditClarificationModalProps {
	isOpen: boolean;
	clarification: ReviewClarification | null;
	onClose: () => void;
	onSave: (clarificationId: number, text: string) => void;
	isLoading: boolean;
}

export const EditClarificationModal: React.FC<EditClarificationModalProps> = ({
	isOpen,
	clarification,
	onClose,
	onSave,
	isLoading,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editText, setEditText] = useState("");

	useEffect(() => {
		if (isOpen && clarification) {
			setEditText(clarification.clarification_text);
			setIsEditing(false);
		}
	}, [isOpen, clarification]);

	if (!clarification) {
		return null;
	}

	const hasBeenEdited =
		clarification.original_clarification_text !== null &&
		clarification.original_clarification_text !== clarification.clarification_text;

	const canEdit = clarification.triage_status === "accepted";
	const hasUnsavedChanges = editText !== clarification.clarification_text;

	const handleSave = () => {
		if (!hasUnsavedChanges) {
			return;
		}
		onSave(clarification.id, editText);
	};

	const handleCancelEdit = () => {
		setEditText(clarification.clarification_text);
		setIsEditing(false);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
			title={`Clarification #${clarification.reference_number}`}
			description={`${clarification.supplier_name}`}
			modalType="standard"
			iconType="info"
			closeOnEsc={!isEditing}
			closeOnOverlayClick={!isEditing}
			primaryButton={
				isEditing
					? {
						onClick: handleSave,
						text: "Save Changes",
						size: "sm",
						width: "auto",
						disabled: !hasUnsavedChanges || isLoading,
					}
					: undefined
			}
			secondaryButton={{
				onClick: isEditing ? handleCancelEdit : onClose,
				text: isEditing ? "Cancel" : "Close",
				size: "sm",
				width: "auto",
			}}
		>
			<div className="space-y-4" data-testid="edit-clarification-modal">
				{/* Header info */}
				<div className="flex items-center gap-3">
					<TriageStatusBadge status={clarification.triage_status} />
					<span className="text-xs text-gray-400">
						Relates to: {clarification.relates_to || "—"}
					</span>
					{clarification.created_by && (
						<span className="text-xs text-gray-400">
							Proposed by {clarification.created_by.name}
							{clarification.created_by.company_name && ` (${clarification.created_by.company_name})`}
						</span>
					)}
				</div>

				{/* Edit mode */}
				{isEditing ? (
					<div className="space-y-2" data-testid="edit-mode">
						<label htmlFor="edit-clarification-text" className="text-xs font-medium uppercase tracking-wider text-gray-500">
							Edit Clarification Text
						</label>
						<textarea
							id="edit-clarification-text"
							className="w-full rounded-lg border border-gray-300 p-3 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
							rows={6}
							value={editText}
							onChange={(e) => setEditText(e.target.value)}
							data-testid="edit-clarification-textarea"
						/>
						{clarification.original_clarification_text && (
							<p className="text-xs text-gray-400">
								Original text is preserved for comparison. Your edits will be tracked.
							</p>
						)}
					</div>
				) : (
					<>
						{/* View mode — show change tracking if edited, otherwise show text */}
						{hasBeenEdited ? (
							<ChangeTrackingView clarification={clarification} />
						) : (
							<div className="rounded-lg border border-gray-200 p-4" data-testid="clarification-text-view">
								<p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">
									Clarification Text
								</p>
								<p className="text-sm text-gray-700 whitespace-pre-wrap">
									{clarification.clarification_text}
								</p>
							</div>
						)}

						{/* Edit button — only for accepted clarifications */}
						{canEdit && (
							<button
								type="button"
								onClick={() => setIsEditing(true)}
								className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
								data-testid="start-edit-btn"
							>
								<FiEdit2 size={12} />
								Edit Clarification Text
							</button>
						)}
					</>
				)}

				{/* Decline reason if declined */}
				{clarification.decline_reason && (
					<div className="rounded-lg border border-red-100 bg-red-50 p-3">
						<p className="text-xs font-medium text-red-600">Decline Reason</p>
						<p className="mt-0.5 text-sm text-red-700">{clarification.decline_reason}</p>
					</div>
				)}
			</div>
		</Modal>
	);
};
